import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AreaPractica, Materia, Prioridad, Usuario } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { CalendarioMensual } from "../../components/CalendarioMensual";
import { Cargando } from "../../components/Cargando";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Input, Select } from "../../components/Formulario";
import { Icono } from "../../components/Icono";
import { Modal } from "../../components/Modal";
import { PanelLateral } from "../../components/PanelLateral";
import { periodoInicial, SelectorPeriodo, type PeriodoSeleccionado } from "../../components/SelectorPeriodo";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { fechaHoyEnZonaSistema } from "../../../shared/timezone";
import { api, ErrorApi } from "../../lib/apiClient";
import { COLOR_POR_TIPO_EVENTO_TRABAJO, COLOR_POR_DISPONIBILIDAD, resolverColorSemantico } from "../../lib/colorSemantico";
import { guardarEstadoVisual, leerEstadoVisual } from "../../lib/estadoVisualSesion";
import { calcularRangoPeriodo, type TipoPeriodo } from "../../lib/periodo";
import { CargaEquipoTabla, type CargaTrabajoAbogado } from "./CargaEquipoTabla";
import { ColaTrabajoVista } from "./ColaTrabajoVista";
import {
  construirColaTrabajo,
  type ActuacionApi,
  type CaducidadApi,
  type ActuacionPendienteApi,
  type EtapaProcesoApi,
  type EventoAgendaApi,
  type HonorarioPendienteApi,
  type SinMovimientoApi,
} from "./colaTrabajo";
import { PlanificacionColapsada } from "./PlanificacionColapsada";

type TipoEventoTrabajo = "Audiencia" | "Vencimiento" | "Escrito" | "Tarea" | "Recordatorio";

interface Disponibilidad {
  nivel: "Disponible" | "Riesgo Medio" | "No recomendable";
  explicacion: string;
}

// Forma completa de GET /mi-trabajo — extendida (spec §10) con las listas de
// Centro de Operaciones que antes no se reenviaban, más la lista individual
// de honorarios pendientes (ver functions/_app/routes/mi-trabajo.ts).
interface DatosMiTrabajo {
  eventosPeriodo: EventoAgendaApi[];
  actuacionesPeriodo: ActuacionApi[];
  atrasados: EventoAgendaApi[];
  misExpedientes: { id: string; numero_expediente: string }[];
  resumen: {
    audiencias: number;
    escritos: number;
    tareas: number;
    vencimientos: number;
    recordatorios: number;
    expedientesConActividad: number;
    honorariosPorCobrar: { moneda: string; total: number }[];
  };
  disponibilidad: Disponibilidad;
  caducidades: CaducidadApi[];
  escritosPendientes: ActuacionPendienteApi[];
  clientesEsperandoRespuesta: ActuacionPendienteApi[];
  expedientesSinMovimiento: SinMovimientoApi[];
  periciasPendientes: ActuacionPendienteApi[];
  oficiosSinRespuesta: ActuacionPendienteApi[];
  notificacionesPendientes: ActuacionPendienteApi[];
  etapasSinResponsable: EtapaProcesoApi[];
  etapasVencidas: EtapaProcesoApi[];
  etapasPorVencer: EtapaProcesoApi[];
  honorariosPendientesLista: HonorarioPendienteApi[];
}

interface VentanaLibre {
  desde: string;
  hasta: string;
  cargaTotal: number;
}

const PRIORIDADES: Prioridad[] = ["Alta", "Media", "Baja"];
const ESTADOS_FILTRO = ["Pendiente", "Vencido"];
const TIPOS_FILTRO: TipoEventoTrabajo[] = ["Audiencia", "Vencimiento", "Escrito", "Tarea", "Recordatorio"];
const CHIP_NEUTRO = "bg-secundario/10 text-secundario";
const CLAVE_SCROLL = "miTrabajo.scroll";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
function formatearFecha(fecha: string): string {
  const [, mes, dia] = fecha.split("-").map(Number);
  return `${dia} de ${MESES[mes - 1]}`;
}

function tipoDeActuacion(a: { tipo: string }): TipoEventoTrabajo {
  if (a.tipo === "Audiencia") return "Audiencia";
  if (a.tipo === "Escrito") return "Escrito";
  return "Tarea";
}
function tipoDeEvento(e: { tipo_evento: string }): TipoEventoTrabajo {
  return e.tipo_evento === "Recordatorio" ? "Recordatorio" : "Vencimiento";
}

function Etiqueta({ children }: { children: React.ReactNode }) {
  return <span className="mb-1 block text-sm font-medium text-texto">{children}</span>;
}

function DetalleDia({
  fecha,
  eventos,
  actuaciones,
}: {
  fecha: string;
  eventos: EventoAgendaApi[];
  actuaciones: ActuacionApi[];
}) {
  const eventosDia = eventos.filter((e) => e.fecha_vencimiento === fecha);
  const actuacionesDia = actuaciones.filter((a) => a.fecha === fecha);
  if (eventosDia.length === 0 && actuacionesDia.length === 0) {
    return <EstadoVacio titulo="Sin actividad este día" />;
  }
  return (
    <ul className="divide-y divide-borde">
      {eventosDia.map((e) => (
        <li key={e.id} className="py-3 text-sm">
          <span className={`mr-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${resolverColorSemantico(COLOR_POR_TIPO_EVENTO_TRABAJO[tipoDeEvento(e)]).chip}`}>
            {tipoDeEvento(e)}
          </span>
          <Link to={`/expedientes/${e.expediente_id}?tab=agenda`} className="font-medium text-texto hover:text-secundario hover:underline">
            {e.titulo}
          </Link>
          <p className="text-xs text-texto-secundario">{e.numero_expediente}</p>
        </li>
      ))}
      {actuacionesDia.map((a) => (
        <li key={a.id} className="py-3 text-sm">
          <span className={`mr-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${resolverColorSemantico(COLOR_POR_TIPO_EVENTO_TRABAJO[tipoDeActuacion(a)]).chip}`}>
            {tipoDeActuacion(a)}
          </span>
          <Link to={`/expedientes/${a.expediente_id}?tab=actuaciones`} className="font-medium text-texto hover:text-secundario hover:underline">
            {a.numero_expediente}
          </Link>
          <p className="text-xs text-texto-secundario">{a.descripcion}</p>
        </li>
      ))}
    </ul>
  );
}

// "Mi Trabajo": antes un dashboard de KPIs, ahora una cola de trabajo
// accionable (ver src/modules/mi-trabajo/colaTrabajo.ts para la lógica pura
// de combinación/agrupación). Período, filtros, vista y el acordeón de
// Planificación viven en la URL (useEstadoEnUrl) para sobrevivir a un
// "volver" desde la ficha de un expediente — al re-montar, el useEffect de
// carga vuelve a pedirle los datos al backend (nunca se asume que algo
// resuelto ya desapareció de un estado en memoria vieja).
export function MiTrabajoPage() {
  const { permisos } = useAuth();
  const veTodos = permisos.has("expedientes.ver_todos");

  const [pTipo, setPTipo] = useEstadoEnUrl("p_tipo", "hoy");
  const [pDesde, setPDesde] = useEstadoEnUrl("p_desde", "");
  const [pHasta, setPHasta] = useEstadoEnUrl("p_hasta", "");
  const periodo: PeriodoSeleccionado = useMemo(() => {
    if (pDesde && pHasta) return { tipo: (pTipo || "hoy") as TipoPeriodo, desde: pDesde, hasta: pHasta };
    return pTipo ? { tipo: pTipo as TipoPeriodo, ...calcularRangoPeriodo(pTipo as TipoPeriodo) } : periodoInicial();
  }, [pTipo, pDesde, pHasta]);
  function setPeriodo(p: PeriodoSeleccionado) {
    setPTipo(p.tipo);
    setPDesde(p.desde);
    setPHasta(p.hasta);
  }

  const [datos, setDatos] = useState<DatosMiTrabajo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useEstadoEnUrl("vista", "cola");
  const [planificacionAbierta, setPlanificacionAbierta] = useEstadoEnUrl("planificacion", "");
  const [diaSeleccionado, setDiaSeleccionado] = useState<string | null>(null);

  const [responsableId, setResponsableId] = useEstadoEnUrl("responsable", "");
  const [materiaId, setMateriaId] = useEstadoEnUrl("materia", "");
  const [areaPracticaId, setAreaPracticaId] = useEstadoEnUrl("unidad", "");
  const [prioridad, setPrioridad] = useEstadoEnUrl("prioridad", "");
  const [estado, setEstado] = useEstadoEnUrl("estado", "");
  const [tipoFiltro, setTipoFiltro] = useEstadoEnUrl("tipo", "");
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [materias, setMaterias] = useState<Materia[]>([]);
  const [areas, setAreas] = useState<AreaPractica[]>([]);

  const [dias, setDias] = useState("10");
  const [buscandoVentana, setBuscandoVentana] = useState(false);
  const [ventana, setVentana] = useState<VentanaLibre | null>(null);
  const [errorVentana, setErrorVentana] = useState<string | null>(null);

  const [cargaTrabajo, setCargaTrabajo] = useState<CargaTrabajoAbogado[]>([]);
  const [ordenCarga, setOrdenCarga] = useState<keyof CargaTrabajoAbogado>("audiencias");

  const parametros = useMemo(() => {
    const p = new URLSearchParams();
    p.set("desde", periodo.desde);
    p.set("hasta", periodo.hasta);
    if (veTodos && responsableId) p.set("responsable_id", responsableId);
    if (materiaId) p.set("materia_id", materiaId);
    if (areaPracticaId) p.set("area_practica_id", areaPracticaId);
    if (prioridad) p.set("prioridad", prioridad);
    if (estado) p.set("estado", estado);
    if (tipoFiltro) p.set("tipo", tipoFiltro);
    return p.toString();
  }, [periodo, veTodos, responsableId, materiaId, areaPracticaId, prioridad, estado, tipoFiltro]);

  function cargar() {
    api
      .get<DatosMiTrabajo>(`/mi-trabajo?${parametros}`)
      .then(setDatos)
      .catch(() => setError("No se pudo cargar Mi Trabajo."));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [parametros]);

  useEffect(() => {
    if (!veTodos) return;
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items.filter((u) => u.activo)))
      .catch(() => undefined);
  }, [veTodos]);

  useEffect(() => {
    api.get<{ items: Materia[] }>("/catalogos/materias").then((d) => setMaterias(d.items)).catch(() => undefined);
    api
      .get<{ items: AreaPractica[] }>("/catalogos-estudio/areas-practica")
      .then((d) => setAreas(d.items.filter((a) => a.activo)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (vista !== "carga" || !veTodos) return;
    api
      .get<{ items: CargaTrabajoAbogado[] }>(`/mi-trabajo/carga-trabajo?desde=${periodo.desde}&hasta=${periodo.hasta}`)
      .then((d) => setCargaTrabajo(d.items))
      .catch(() => setCargaTrabajo([]));
  }, [vista, veTodos, periodo]);

  // Restaura el scroll guardado al volver a entrar (una sola vez, cuando ya
  // hay contenido montado) — y lo va guardando mientras el usuario navega la
  // página, para que "volver" desde un expediente lo deje donde estaba.
  const hayDatos = Boolean(datos);
  useEffect(() => {
    if (!hayDatos) return;
    const guardado = leerEstadoVisual<number>(CLAVE_SCROLL, 0);
    if (guardado > 0) window.scrollTo(0, guardado);
    // Sólo al primer render con datos — no en cada actualización posterior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hayDatos]);

  useEffect(() => {
    function alScrollear() {
      guardarEstadoVisual(CLAVE_SCROLL, window.scrollY);
    }
    window.addEventListener("scroll", alScrollear, { passive: true });
    return () => window.removeEventListener("scroll", alScrollear);
  }, []);

  const chips = useMemo(() => {
    const items: { clave: string; etiqueta: string; clase: string; onQuitar: () => void }[] = [];
    if (veTodos && responsableId) {
      const nombre = usuarios.find((u) => u.id === responsableId)?.nombre_completo ?? responsableId;
      items.push({ clave: "responsable", etiqueta: `Responsable: ${nombre}`, clase: CHIP_NEUTRO, onQuitar: () => setResponsableId("") });
    }
    if (materiaId) {
      const nombre = materias.find((m) => m.id === materiaId)?.nombre ?? materiaId;
      items.push({ clave: "materia", etiqueta: `Materia: ${nombre}`, clase: CHIP_NEUTRO, onQuitar: () => setMateriaId("") });
    }
    if (areaPracticaId) {
      const area = areas.find((a) => a.id === areaPracticaId);
      items.push({
        clave: "area",
        etiqueta: `Unidad: ${area?.nombre ?? areaPracticaId}`,
        clase: resolverColorSemantico(area?.color).chip,
        onQuitar: () => setAreaPracticaId(""),
      });
    }
    if (prioridad) items.push({ clave: "prioridad", etiqueta: `Prioridad: ${prioridad}`, clase: CHIP_NEUTRO, onQuitar: () => setPrioridad("") });
    if (estado) items.push({ clave: "estado", etiqueta: `Estado: ${estado}`, clase: CHIP_NEUTRO, onQuitar: () => setEstado("") });
    if (tipoFiltro) {
      items.push({
        clave: "tipo",
        etiqueta: `Tipo: ${tipoFiltro}`,
        clase: resolverColorSemantico(COLOR_POR_TIPO_EVENTO_TRABAJO[tipoFiltro]).chip,
        onQuitar: () => setTipoFiltro(""),
      });
    }
    return items;
  }, [veTodos, responsableId, materiaId, areaPracticaId, prioridad, estado, tipoFiltro, usuarios, materias, areas]);

  function limpiarFiltros() {
    setResponsableId("");
    setMateriaId("");
    setAreaPracticaId("");
    setPrioridad("");
    setEstado("");
    setTipoFiltro("");
  }

  async function buscarVentana() {
    setBuscandoVentana(true);
    setErrorVentana(null);
    setVentana(null);
    try {
      const respuesta = await api.get<{ ventana: VentanaLibre | null }>(`/mi-trabajo/ventana-libre?dias=${dias}`);
      if (!respuesta.ventana) {
        setErrorVentana("No se encontró una ventana disponible para esa cantidad de días.");
      } else {
        setVentana(respuesta.ventana);
      }
    } catch (err) {
      setErrorVentana(err instanceof ErrorApi ? err.message : "No se pudo buscar la ventana libre.");
    } finally {
      setBuscandoVentana(false);
    }
  }

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;

  const hoyIso = fechaHoyEnZonaSistema();
  const grupos = datos ? construirColaTrabajo(datos, hoyIso) : [];

  const eventosCalendario = datos
    ? [
        ...datos.eventosPeriodo.map((e) => ({ fecha: e.fecha_vencimiento, tipo: tipoDeEvento(e) })),
        ...datos.actuacionesPeriodo.map((a) => ({ fecha: a.fecha, tipo: tipoDeActuacion(a) })),
      ]
    : [];

  const nombreResponsableActivo =
    veTodos && responsableId ? usuarios.find((u) => u.id === responsableId)?.nombre_completo ?? null : null;

  return (
    <div>
      <EncabezadoPagina titulo="Mi Trabajo" descripcion="Tu cola de trabajo: qué hacer, no sólo cuánto queda." />

      {/* 1. Período y filtros */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <SelectorPeriodo valor={periodo} onCambiar={setPeriodo} />
        <button
          type="button"
          onClick={() => setFiltrosAbiertos(true)}
          className="flex items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 text-sm font-medium text-texto transition-colors hover:bg-fondo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
        >
          <Icono nombre="filtro" className="h-4 w-4" />
          Filtros
          {chips.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-secundario px-1.5 text-xs font-semibold text-superficie">
              {chips.length}
            </span>
          )}
        </button>
      </div>

      {chips.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <span key={chip.clave} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${chip.clase}`}>
              {chip.etiqueta}
              <button
                type="button"
                onClick={chip.onQuitar}
                aria-label={`Quitar filtro ${chip.etiqueta}`}
                className="rounded-full opacity-70 hover:opacity-100"
              >
                <Icono nombre="cerrar" className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={limpiarFiltros}
            className="text-xs font-medium text-texto-secundario underline-offset-2 hover:text-texto hover:underline"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {/* Los filtros rápidos (materia/unidad/prioridad/estado/tipo) sólo
          acotan el período (Sección B) — nunca lo urgente/atrasado, que se
          muestra siempre. Ver colaTrabajo.ts. */}
      <PanelLateral titulo="Filtros" abierto={filtrosAbiertos} onCerrar={() => setFiltrosAbiertos(false)}>
        <div className="space-y-4">
          {veTodos && (
            <label className="block">
              <Etiqueta>Responsable</Etiqueta>
              <Select value={responsableId} onChange={(e) => setResponsableId(e.target.value)}>
                <option value="">Yo mismo</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre_completo}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <label className="block">
            <Etiqueta>Materia</Etiqueta>
            <Select value={materiaId} onChange={(e) => setMateriaId(e.target.value)}>
              <option value="">Toda materia</option>
              {materias.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <Etiqueta>Unidad de Negocio</Etiqueta>
            <Select value={areaPracticaId} onChange={(e) => setAreaPracticaId(e.target.value)}>
              <option value="">Toda unidad</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <Etiqueta>Prioridad</Etiqueta>
            <Select value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>
              <option value="">Toda prioridad</option>
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <Etiqueta>Estado</Etiqueta>
            <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todo estado</option>
              {ESTADOS_FILTRO.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <Etiqueta>Tipo de evento</Etiqueta>
            <Select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
              <option value="">Todo tipo</option>
              {TIPOS_FILTRO.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </label>
          {chips.length > 0 && (
            <Boton variante="secundario" onClick={limpiarFiltros} className="w-full">
              Limpiar filtros
            </Boton>
          )}
        </div>
      </PanelLateral>

      {!datos ? (
        <Cargando />
      ) : (
        <>
          {nombreResponsableActivo && (
            <p className="mb-4 text-sm text-texto-secundario">
              Mostrando el trabajo de <span className="font-medium text-texto">{nombreResponsableActivo}</span>.
            </p>
          )}

          {/* 2. Contexto breve de disponibilidad */}
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-borde bg-superficie px-4 py-3">
            <Icono nombre="balanza" className="h-5 w-5 flex-shrink-0 text-texto-secundario" />
            <span
              className={`inline-flex flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${resolverColorSemantico(COLOR_POR_DISPONIBILIDAD[datos.disponibilidad.nivel]).chip}`}
            >
              {datos.disponibilidad.nivel}
            </span>
            <span className="text-sm text-texto-secundario">{datos.disponibilidad.explicacion}</span>
          </div>

          {/* 3. Cola de trabajo */}
          <div className="mb-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-texto">Cola de trabajo</h2>
            <p className="mt-1 text-sm text-texto-secundario">
              Lo urgente y atrasado se muestra siempre, sin importar el período elegido. El período sólo filtra
              audiencias, vencimientos, escritos, tareas y honorarios programados dentro de ese rango.
            </p>
          </div>
          <div className="mb-8 mt-4">
            <ColaTrabajoVista
              grupos={grupos}
              hoyIso={hoyIso}
              puedeEditarAgenda={permisos.has("agenda.editar")}
              onAccionCompletada={cargar}
            />
          </div>

          {/* 4. Planificación (colapsada por defecto) */}
          <PlanificacionColapsada abierto={planificacionAbierta === "abierta"} onCambiar={(v) => setPlanificacionAbierta(v ? "abierta" : "")}>
            <section>
              <h3 className="mb-3 text-sm font-semibold text-texto">Buscador de ventana libre</h3>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <Etiqueta>Días</Etiqueta>
                  <Input type="number" min={1} max={120} value={dias} onChange={(e) => setDias(e.target.value)} className="w-24" />
                </label>
                <Boton onClick={buscarVentana} cargando={buscandoVentana}>
                  Buscar
                </Boton>
              </div>
              {errorVentana && <p className="mt-3 text-sm text-error">{errorVentana}</p>}
              {ventana && (
                <p className="mt-3 text-sm text-texto">
                  La mejor ventana disponible es del {formatearFecha(ventana.desde)} al {formatearFecha(ventana.hasta)}.
                </p>
              )}
            </section>
            <section>
              <h3 className="mb-3 text-sm font-semibold text-texto">Calendario del período</h3>
              <CalendarioMensual
                desde={periodo.desde}
                hasta={periodo.hasta}
                eventos={eventosCalendario}
                diaSeleccionado={diaSeleccionado}
                onSeleccionarDia={setDiaSeleccionado}
              />
            </section>
          </PlanificacionColapsada>

          {/* 5. Accesos secundarios */}
          <div className="flex flex-wrap items-center gap-3 border-t border-borde pt-6">
            <Link
              to="/expedientes"
              className="flex items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 text-sm font-medium text-texto transition-colors hover:bg-fondo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
            >
              <Icono nombre="expedientes" className="h-4 w-4" />
              Ver mis expedientes ({datos.misExpedientes.length})
            </Link>
            {veTodos && (
              <button
                type="button"
                onClick={() => setVista(vista === "carga" ? "cola" : "carga")}
                aria-pressed={vista === "carga"}
                className={`flex items-center gap-2 rounded-lg border border-borde px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2 ${
                  vista === "carga" ? "bg-principal text-superficie" : "bg-superficie text-texto hover:bg-fondo"
                }`}
              >
                <Icono nombre="usuarios" className="h-4 w-4" />
                Ver carga del equipo
              </button>
            )}
          </div>

          {vista === "carga" && veTodos && (
            <div className="mt-6">
              <CargaEquipoTabla filas={cargaTrabajo} orden={ordenCarga} onCambiarOrden={setOrdenCarga} />
            </div>
          )}

          <Modal
            titulo={diaSeleccionado ? `Detalle del ${formatearFecha(diaSeleccionado)}` : "Detalle"}
            abierto={Boolean(diaSeleccionado)}
            onCerrar={() => setDiaSeleccionado(null)}
          >
            {diaSeleccionado && (
              <DetalleDia fecha={diaSeleccionado} eventos={datos.eventosPeriodo} actuaciones={datos.actuacionesPeriodo} />
            )}
          </Modal>
        </>
      )}
    </div>
  );
}
